import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaShieldAlt } from 'react-icons/fa';
import styled from 'styled-components';
import { API_URL } from '../socket';
import { AdminMovesEditor } from '../components/Admin/AdminMovesEditor';

const ADMIN_TOKEN_KEY = 'movie-mayhem.admin-token';
const ADMIN_USERNAME_KEY = 'movie-mayhem.admin-username';

const Page = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(ellipse 70% 50% at 50% -10%, rgba(255, 177, 60, 0.08) 0%, transparent 70%),
    radial-gradient(ellipse 60% 60% at 15% 25%, rgba(193, 39, 45, 0.16) 0%, transparent 70%),
    var(--color-ink-950);
  color: var(--color-cream-100);
  padding: clamp(20px, 4vh, 40px) var(--space-6) var(--space-10);
`;

const Shell = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Top = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-200);
  text-decoration: none;
  &:hover {
    color: var(--color-amber-500);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 44px);
  font-style: italic;
  color: var(--color-amber-500);
  letter-spacing: -0.01em;
`;

const Sub = styled.p`
  margin: var(--space-2) 0 0;
  font-size: 13px;
  color: var(--color-cream-400);
  max-width: 520px;
`;

const Card = styled.section`
  background: rgba(8, 7, 10, 0.72);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
  padding: var(--space-5);
  margin-bottom: var(--space-5);
  position: relative;
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: var(--color-amber-500);
    opacity: 0.9;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: var(--space-6);
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const CardTitle = styled.h2`
  margin: 0 0 var(--space-4);
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-amber-500);
`;

const CardBlurb = styled.p`
  margin: -6px 0 var(--space-4);
  font-size: 13px;
  color: var(--color-cream-300);
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-width: 360px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: var(--font-heading);
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-400);
`;

const Input = styled.input`
  padding: 10px 12px;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--color-cream-100);
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-700);
  outline: none;
  &:focus {
    border-color: var(--color-amber-500);
  }
`;

const PasswordWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
`;

const IconBtn = styled.button`
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-700);
  color: var(--color-cream-300);
  cursor: pointer;
  clip-path: var(--clip-button);
  transition: color var(--dur-fast) var(--ease-out),
              border-color var(--dur-fast) var(--ease-out);

  &:hover,
  &:focus-visible {
    color: var(--color-amber-500);
    border-color: var(--color-amber-500);
    outline: none;
  }
`;

const Btn = styled.button`
  padding: 12px 20px;
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  border: 1px solid var(--color-amber-500);
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  cursor: pointer;
  clip-path: var(--clip-button);
  align-self: flex-start;
  &:hover {
    filter: brightness(1.06);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled(Btn)`
  background: transparent;
  color: var(--color-cream-200);
  border-color: var(--color-ink-600);
`;

const Err = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--color-red-400);
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: var(--space-4);
`;

const Tab = styled.button`
  padding: 10px 18px;
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  border: 1px solid ${(p) => (p.$active ? 'var(--color-amber-500)' : 'var(--color-ink-600)')};
  background: ${(p) =>
    p.$active
      ? 'linear-gradient(180deg, rgba(255, 177, 60, 0.16), rgba(255, 177, 60, 0.06))'
      : 'transparent'};
  color: ${(p) => (p.$active ? 'var(--color-amber-500)' : 'var(--color-cream-300)')};
  cursor: pointer;
  clip-path: var(--clip-button);
  &:hover {
    border-color: var(--color-amber-500);
    color: var(--color-amber-500);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 10px 12px;
  background: linear-gradient(180deg, rgba(8, 7, 10, 0.92) 0%, rgba(8, 7, 10, 0.7) 100%);
  border: 1px solid var(--color-ink-800);
  clip-path: var(--clip-card);
  backdrop-filter: blur(8px);
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-family: var(--font-heading);
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-200);
  background: rgba(8, 7, 10, 0.5);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-button);

  svg {
    color: var(--color-amber-500);
  }
`;

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [username, setUsername] = useState(() => sessionStorage.getItem(ADMIN_USERNAME_KEY) || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginErr, setLoginErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('moves');

  const signedInLabel = useMemo(() => {
    if (!token) return null;
    const user = username || 'admin';
    return `Signed in: ${user}`;
  }, [token, username]);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken('');
  }, []);

  const logout = async () => {
    const t = sessionStorage.getItem(ADMIN_TOKEN_KEY);
    if (t) {
      try {
        await fetch(`${API_URL}/api/admin/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${t}` },
        });
      } catch (_) {}
    }
    clearSession();
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginErr(null);
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setLoginErr(data.error || 'Login failed');
        return;
      }
      sessionStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      sessionStorage.setItem(ADMIN_USERNAME_KEY, username);
      setToken(data.token);
      setPassword('');
    } catch (err) {
      setLoginErr(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const onSessionExpired = useCallback(() => {
    clearSession();
    setLoginErr('Session expired. Sign in again.');
  }, [clearSession]);

  return (
    <Page>
      <Shell>
        <Top>
          <TitleBlock>
            <Back to="/">
              <FaArrowLeft /> Home
            </Back>
            <Title>Admin</Title>
            <Sub>Operator tools — sign in to manage live data.</Sub>
          </TitleBlock>
        </Top>

        {!token ? (
          <Card>
            <CardGrid>
              <div>
                <CardTitle>Sign in</CardTitle>
                <CardBlurb>
                  This area controls live game data (moves, limits, effects, and GIFs). Keep access tight.
                </CardBlurb>
                {loginErr && <Err>{loginErr}</Err>}
                <Form onSubmit={onLogin}>
                  <FieldRow>
                    <Label>
                      Username
                      <Input
                        autoComplete="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter admin username"
                      />
                    </Label>
                    <Label>
                      Password
                      <PasswordWrap>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter admin password"
                        />
                        <IconBtn
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </IconBtn>
                      </PasswordWrap>
                    </Label>
                  </FieldRow>
                  <Btn type="submit" disabled={loading || !username || !password}>
                    {loading ? 'Signing in…' : 'Sign in'}
                  </Btn>
                </Form>
              </div>
              <div>
                <CardTitle>Session</CardTitle>
                <CardBlurb>
                  Sessions last about <strong>24 hours</strong> (or until you sign out). If you see “Session expired”,
                  just log in again.
                </CardBlurb>
                <Badge>
                  <FaShieldAlt aria-hidden />
                  Admin-only
                </Badge>
              </div>
            </CardGrid>
          </Card>
        ) : (
          <>
            <Toolbar>
              <TabRow role="tablist" aria-label="Admin sections">
                <Tab type="button" $active={tab === 'moves'} onClick={() => setTab('moves')} role="tab">
                  Moves
                </Tab>
              </TabRow>
              {signedInLabel && <Badge><FaShieldAlt aria-hidden />{signedInLabel}</Badge>}
              <GhostBtn type="button" onClick={logout}>
                Sign out
              </GhostBtn>
            </Toolbar>

            {tab === 'moves' && (
              <Card>
                <CardTitle>Moves & GIFs</CardTitle>
                <AdminMovesEditor token={token} onSessionExpired={onSessionExpired} />
              </Card>
            )}
          </>
        )}
      </Shell>
    </Page>
  );
}
